import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import FindInPageRounded from '@mui/icons-material/FindInPageRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import { BarList, EmptyState, MetricCard, Panel, type MetricTrend } from '../../../ui-kit';
import { fetchPcSpotlight, type PcDay, type PcSpotlight } from '../../../services/policyCheckingService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { useLive } from '../rocco/live';

const whole = (v: number) => v.toLocaleString();

/** "$4.3M", "$860k", "$0" — cover reads better rounded than in full. */
const money = (value: number): string => {
  if (!value) return '$0';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value)}`;
};

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: PcSpotlight = {
  flow: { documents_this_week: 0, last_run_at: null, error: null, reading_now: 0 },
  tiles: {
    checks: [], must_fix: [], review: [], all_clear: [], documents: 0,
    totals: { checked: 0, problems: 0, with_a_problem: 0, cover_at_risk: 0, fields_checked: 0 },
  },
  caught: [],
  results: [],
};

/**
 * The page Policy Checking opens on. Every figure is read from the checks that
 * have actually been run.
 *
 * Top to bottom: how a policy's paperwork moves through it, the week in numbers, then
 * what it has been catching — which is the part that makes the case.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { data, error } = useLive(fetchPcSpotlight, 10_000);

  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const { tiles } = data ?? EMPTY;
  const totals = tiles.totals;

  // What a policy's paperwork goes through, said the way somebody in the business would
  // say it. How the agent does any of it belongs on Agent Configuration.
  const nodes: FlowNode[] = [
    {
      id: 'upload',
      step: 'Documents in',
      name: "The policy's paperwork",
      detail: 'Quote, application and issued policy',
      logo: <UploadFileRounded sx={{ fontSize: 20 }} />,
      to: 'new',
    },
    {
      id: 'reads',
      step: 'Reads them',
      name: 'Every detail on each page',
      detail: 'Whatever the carrier sent',
      logo: <FindInPageRounded sx={{ fontSize: 20 }} />,
      to: 'checks',
    },
    {
      id: 'compares',
      step: 'Compares',
      name: 'What was issued against what was applied for',
      detail: 'And against our own records',
      logo: <CompareArrowsRounded sx={{ fontSize: 20 }} />,
      to: 'configuration',
    },
    {
      id: 'flags',
      step: 'Flags',
      name: 'Anything that disagrees',
      detail: 'To fix, or to look at',
      logo: <FlagRounded sx={{ fontSize: 20 }} />,
      to: 'checks',
    },
  ];

  const trend = (points: PcDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  return (
    <Stack spacing={2}>
      <AgentFlow
        nodes={nodes}
        loading={loading}
        subtitle="From the documents landing to everything that disagrees, flagged"
        onOpen={(to) => navigate(`/agents/policy_checking/${to}`)}
      />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard loading={loading} label="Policies checked by AI" value={whole(totals.checked)}
          info="Policies read and compared against what was quoted and applied for."
          trend={trend(tiles.checks, 'Checks by day, this week')} />
        <MetricCard loading={loading} label="Problems found by AI" value={whole(totals.problems)}
          info="Things that must be put right — a face amount cut, a rider dropped, a name wrong."
          trend={trend(tiles.must_fix, 'Must fix by day, this week')} />
        <MetricCard loading={loading} label="Policies with a problem"
          value={totals.checked ? `${totals.with_a_problem} of ${totals.checked}` : '—'}
          info="How many of the policies checked were not what was applied for." />
        <MetricCard loading={loading} label="Cover at risk" value={money(totals.cover_at_risk)}
          info="The face amount on the policies that must be put right — what is riding on fixing them." />
        <MetricCard loading={loading} label="Fields checked by AI" value={whole(totals.fields_checked)}
          info="Every field compared across the documents, the ones that agreed as well as the ones that did not." />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What it caught" subtitle="By field, across every check" loading={loading} loadingHeight={200}>
          {data?.caught.length ? (
            <BarList
              rows={data.caught.map((row) => ({ key: row.label, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Times"
              labelHeading="Field"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              Nothing flagged yet.
            </Typography>
          )}
        </Panel>
        <Panel title="How the checks came out" subtitle="Every check so far" loading={loading} loadingHeight={200}>
          {data?.results.some((r) => r.value) ? (
            <BarList
              rows={data.results.map((row) => ({ key: row.label, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Checks"
              labelHeading="Result"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No checks have finished yet.
            </Typography>
          )}
        </Panel>
      </Box>
    </Stack>
  );
}
